import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader, X, Maximize2, Download, Printer, RotateCw, RefreshCw } from 'lucide-react';
import { documentsApi } from '../services/api';

// Set up PDF.js worker - use CDN for reliable production loading
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * PDFViewer component with field highlighting support
 * Based on the Xtract document-extraction EmailDetail implementation
 *
 * @param {Object} props
 * @param {string} props.documentId - ID of the document to load
 * @param {Object|null} props.highlightedField - Field to highlight (with bounding_box and page_number)
 * @param {function} props.onClose - Callback when viewer is closed
 */
function PDFViewer({ documentId, highlightedField, onClose, onPageChange, fileName }) {
  const containerRef = useRef(null);
  // Per-page DOM refs, keyed by 1-based page number
  const canvasRefs = useRef({});
  const overlayRefs = useRef({});
  const pageWrapperRefs = useRef({});
  const pageViewportsRef = useRef({}); // pageNum -> { viewport, pageView }
  const isProgrammaticScrollRef = useRef(false);
  const scrollResetTimerRef = useRef(null);
  const renderTasksRef = useRef({}); // pageNum -> in-flight pdf.js RenderTask, so we can cancel stale renders
  const renderGenerationRef = useRef(0); // Bumped every time renderAllPages starts, so overlapping calls can detect they're stale and bail out instead of racing/corrupting the canvas

  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(null); // Start with null to prevent premature rendering
  const [fitToWidth, setFitToWidth] = useState(true); // Auto-fit enabled by default
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [baseViewport, setBaseViewport] = useState(null); // Store base viewport for fit calculations
  const [initialFitDone, setInitialFitDone] = useState(false); // Track if initial fit is done
  const [loadingProgress, setLoadingProgress] = useState(''); // Loading progress text
  const [retryCount, setRetryCount] = useState(0); // Retry counter to re-trigger load
  const [highlightRegionsByPage, setHighlightRegionsByPage] = useState({}); // pageNum -> computed highlight regions for hover tooltips
  const [isPanning, setIsPanning] = useState(false); // Click-and-drag panning state
  const [rotation, setRotation] = useState({}); // Per-page manual rotation map: { [pageNum]: degrees (0/90/180/270) }, layered on top of each page's own embedded /Rotate value. Defaults to {} (no override) so pages display exactly as their embedded rotation intends; the rotate button nudges only the CURRENT page, since documents can mix correctly-oriented pages with pages that have no/incorrect rotation metadata.
  const panRef = useRef({ active: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });

  // localStorage key helper for persisting manual rotation choices per document
  const rotationStorageKey = (docId) => `xtract_pdf_rotation_${docId}`;

  // Sync pageInput when currentPage changes (from arrows, highlights, scroll, etc.)
  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  // Notify parent of page changes
  useEffect(() => {
    if (onPageChange) onPageChange(currentPage);
  }, [currentPage, onPageChange]);

  // Scroll a specific page into view (used by prev/next, page-jump, and field highlight navigation)
  const scrollToPage = useCallback((pageNum) => {
    const el = pageWrapperRefs.current[pageNum];
    if (!el) return;
    isProgrammaticScrollRef.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setCurrentPage(pageNum);
    clearTimeout(scrollResetTimerRef.current);
    scrollResetTimerRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 700);
  }, []);

  // Load PDF document
  useEffect(() => {
    if (!documentId) {
      setError('No document ID provided');
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Reset state when document changes
    setFitToWidth(true); // Re-enable fit-to-width for new document
    setScale(null); // Reset to null to wait for fit calculation
    setCurrentPage(1);
    setPdfDoc(null);
    setInitialFitDone(false);
    setLoadingProgress('');
    // Invalidate any in-flight render task/generation from the previous document so a
    // late-resolving render() promise for the OLD document can't paint over the new one's canvas.
    renderGenerationRef.current += 1;
    Object.values(renderTasksRef.current).forEach((task) => {
      if (task) { try { task.cancel(); } catch { /* already finished/cancelled */ } }
    });
    renderTasksRef.current = {};
    pageViewportsRef.current = {};
    // Restore any previously saved per-page manual rotations for this document (if any),
    // otherwise default to {} (i.e. just honor each page's own embedded /Rotate value)
    try {
      const saved = window.localStorage.getItem(rotationStorageKey(documentId));
      const parsed = saved ? JSON.parse(saved) : {};
      setRotation(parsed && typeof parsed === 'object' ? parsed : {});
    } catch {
      setRotation({});
    }

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        let loadingTask;

        // ── Strategy 1: SAS URL (direct from Azure Storage, bypasses backend) ──
        try {
          setLoadingProgress('Getting document URL...');
          const sasResponse = await documentsApi.getSasUrl(documentId, 1); // 1-hour expiry
          const sasUrl = sasResponse.data?.url;

          if (sasUrl && !cancelled) {
            setLoadingProgress('Loading PDF from storage...');
            loadingTask = pdfjsLib.getDocument({
              url: sasUrl,
              disableStream: false,
              disableAutoFetch: false,
              withCredentials: false, // SAS URL doesn't need credentials
            });

            loadingTask.onProgress = (progress) => {
              if (cancelled) return;
              if (progress.total > 0) {
                const pct = Math.round((progress.loaded / progress.total) * 100);
                setLoadingProgress(`Loading PDF... ${pct}%`);
              }
            };

            const pdf = await loadingTask.promise;

            if (cancelled) return;

            console.log('PDFViewer: Loaded via SAS URL');
            setPdfDoc(pdf);
            setTotalPages(pdf.numPages);
            setCurrentPage(1);
            setLoadingProgress('');
            setLoading(false);
            return; // Success - done
          }
        } catch (sasErr) {
          // SAS URL approach failed (CORS not configured, network issue, etc.)
          // Fall through to backend proxy approach
          console.warn('PDFViewer: SAS URL approach failed, falling back to backend proxy:', sasErr.message);
        }

        if (cancelled) return;

        // ── Strategy 2: Backend proxy (fallback) ──
        setLoadingProgress('Downloading document via server...');

        const response = await documentsApi.getPdfBlob(documentId);

        if (cancelled) return;

        const dataSize = response.data?.byteLength || 0;

        if (!response.data || dataSize === 0) {
          throw new Error('Empty PDF content received');
        }

        const sizeMB = (dataSize / (1024 * 1024)).toFixed(1);
        setLoadingProgress(`Parsing PDF (${sizeMB} MB)...`);

        loadingTask = pdfjsLib.getDocument({
          data: response.data,
          // Improve performance for large documents
          disableAutoFetch: dataSize > 5 * 1024 * 1024, // disable auto-fetch for files > 5MB
          disableStream: false,
        });

        loadingTask.onProgress = (progress) => {
          if (cancelled) return;
          if (progress.total > 0) {
            const pct = Math.round((progress.loaded / progress.total) * 100);
            setLoadingProgress(`Parsing PDF... ${pct}%`);
          }
        };

        const pdf = await loadingTask.promise;

        if (cancelled) return;

        console.log('PDFViewer: Loaded via backend proxy');
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
        setLoadingProgress('');
      } catch (err) {
        if (cancelled) return;
        console.error('PDFViewer: Error loading PDF:', err);
        const errorMessage = err.message || 'Unknown error';
        setError(`Failed to load PDF: ${errorMessage}`);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      cancelled = true;
    };
  }, [documentId, retryCount]);

  // Navigate to highlighted field page (all pages are already rendered — just scroll to it)
  useEffect(() => {
    if (highlightedField?.page_number && pdfDoc) {
      const targetPage = highlightedField.page_number;
      if (targetPage >= 1 && targetPage <= totalPages) {
        scrollToPage(targetPage);
      }
    }
  }, [highlightedField?.page_number, highlightedField?.field_name, pdfDoc, totalPages, scrollToPage]);

  // Calculate fit-to-width scale — always based on page 1, since fit shouldn't
  // jump around as the user scrolls past pages of (typically uniform) size.
  const calculateFitScale = useCallback(async (pageNum) => {
    if (!pdfDoc || !containerRef.current) return 1.0;

    try {
      const page = await pdfDoc.getPage(pageNum);
      // Combine the page's own embedded rotation with any manual override for THIS page
      // so fit-to-width accounts for the actual displayed (possibly swapped) width/height
      const pageRotationOverride = rotation[pageNum] || 0;
      const viewport = page.getViewport({ scale: 1.0, rotation: (page.rotate + pageRotationOverride) % 360 });

      // Store base viewport for reference
      setBaseViewport(viewport);

      // Get container width - be conservative with padding/margins
      // Account for: container padding (32px), scrollbar (~17px), and some buffer (10px)
      const containerWidth = containerRef.current.clientWidth - 60;

      // Calculate scale to fit width
      const fitScale = containerWidth / viewport.width;

      // Clamp between reasonable min/max values - allow smaller scales for very wide docs
      return Math.max(0.1, Math.min(fitScale, 3.0));
    } catch (err) {
      console.error('Error calculating fit scale:', err);
      return 1.0;
    }
  }, [pdfDoc, rotation]);

  // Auto-fit when PDF loads (based on page 1 dimensions — pages assumed uniform size)
  // Also re-runs when the manual rotation changes fit-to-width is on, since width/height may have swapped.
  useEffect(() => {
    if (pdfDoc && fitToWidth && containerRef.current) {
      // Use requestAnimationFrame to ensure layout is complete before measuring
      const rafId = requestAnimationFrame(() => {
        setTimeout(() => {
          calculateFitScale(1).then(fitScale => {
            setScale(fitScale);
            setInitialFitDone(true);
          });
        }, 50);
      });
      return () => cancelAnimationFrame(rafId);
    } else if (pdfDoc && !fitToWidth && scale === null) {
      // If fit-to-width is disabled but we don't have a scale, use default 1.0
      setScale(1.0);
      setInitialFitDone(true);
    }
  }, [pdfDoc, fitToWidth, calculateFitScale, rotation]);

  // Handle container resize for fit-to-width. Uses a ResizeObserver (not just
  // a window "resize" listener) so the PDF also rescales when the user drags
  // the split-panel divider to resize the viewer panel itself, not only when
  // the browser window changes size.
  useEffect(() => {
    if (!fitToWidth || !initialFitDone || !containerRef.current) return;

    let rafId = null;
    const recalcFit = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (pdfDoc) {
          calculateFitScale(1).then(fitScale => {
            setScale(prev => (prev !== null && Math.abs(prev - fitScale) < 0.001 ? prev : fitScale));
          });
        }
      });
    };

    const resizeObserver = new ResizeObserver(recalcFit);
    resizeObserver.observe(containerRef.current);
    window.addEventListener('resize', recalcFit);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', recalcFit);
    };
  }, [pdfDoc, fitToWidth, calculateFitScale, initialFitDone]);

  // Render every page's canvas whenever the PDF, zoom scale, or manual rotation changes
  useEffect(() => {
    if (pdfDoc && scale !== null) {
      renderAllPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, scale, totalPages, rotation]);

  // Re-draw just the highlight overlays (cheap) whenever the highlighted field changes
  useEffect(() => {
    if (pdfDoc && scale !== null) {
      renderAllOverlays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedField]);

  // Track which page is most visible while the user scrolls, to keep the
  // toolbar's page indicator in sync (ignored during programmatic scrollIntoView calls)
  useEffect(() => {
    const root = containerRef.current;
    if (!root || totalPages === 0 || scale === null) return;

    const observer = new IntersectionObserver((entries) => {
      if (isProgrammaticScrollRef.current) return;
      let best = null;
      entries.forEach((entry) => {
        if (entry.isIntersecting && (!best || entry.intersectionRatio > best.intersectionRatio)) {
          best = entry;
        }
      });
      if (best) {
        const pageNum = parseInt(best.target.dataset.page, 10);
        if (pageNum) setCurrentPage(pageNum);
      }
    }, { root, threshold: [0.25, 0.5, 0.75] });

    Object.values(pageWrapperRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [totalPages, scale]);

  // Parse D(pageNum, x1,y1, x2,y2, x3,y3, x4,y4) format
  // Handles semicolon-separated multiple D() entries: "D(13,...);D(14,...)"
  const parseBoundingBoxes = (location, pageWidthInches, pageHeightInches) => {
    if (!location || typeof location !== 'string') return [];

    const regions = [];
    const pattern = /D\((\d+),([\d.]+),([\d.]+),([\d.]+),([\d.]+),([\d.]+),([\d.]+),([\d.]+),([\d.]+)\)/g;
    let match;
    while ((match = pattern.exec(location)) !== null) {
      const pageNumber = parseInt(match[1]);
      const points = [
        parseFloat(match[2]), parseFloat(match[3]),  // x1, y1
        parseFloat(match[4]), parseFloat(match[5]),  // x2, y2
        parseFloat(match[6]), parseFloat(match[7]),  // x3, y3
        parseFloat(match[8]), parseFloat(match[9])   // x4, y4
      ];

      // Convert from inches to normalized coordinates (0-1)
      const normalizedPolygon = [];
      for (let i = 0; i < points.length; i += 2) {
        normalizedPolygon.push(points[i] / pageWidthInches);
        normalizedPolygon.push(points[i + 1] / pageHeightInches);
      }

      regions.push({ pageNumber, polygon: normalizedPolygon });
    }

    return regions;
  };

  // Compute the highlight regions (and draw them) for a single page's overlay canvas.
  // Returns the array of hover-tooltip regions for that page (empty if none apply).
  const computeAndDrawHighlightsForPage = (pageNum, viewport, pageView, overlayContext) => {
    const computedRegions = [];
    if (!(highlightedField?.bounding_box || highlightedField?.bounding_boxes)) {
      return computedRegions;
    }
    // Get page dimensions in inches (72 points per inch)
    const pageWidthPt = pageView[2] - pageView[0];
    const pageHeightPt = pageView[3] - pageView[1];
    const pageWidthInches = pageWidthPt / 72;
    const pageHeightInches = pageHeightPt / 72;

    // Collect all bounding boxes to draw
    const boxes = highlightedField.bounding_boxes
      ? highlightedField.bounding_boxes
      : [highlightedField.bounding_box];

    // Field labels array (parallel to bounding_boxes)
    const fieldLabels = highlightedField.field_labels || [];

    boxes.forEach((box, idx) => {
      // Each box string may contain multiple D() entries separated by ;
      // e.g. "D(13,...);D(14,...)" for multi-page/multi-box fields
      const regions = parseBoundingBoxes(box, pageWidthInches, pageHeightInches);
      const label = fieldLabels[idx] || highlightedField.field_name || '';

      regions.forEach((region) => {
        if (region.pageNumber !== pageNum) return;
        drawHighlight(overlayContext, region.polygon, viewport);

        // Compute bounding rect from polygon for hover overlay
        const poly = region.polygon;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (let i = 0; i < poly.length; i += 2) {
          const px = poly[i] * viewport.width;
          const py = poly[i + 1] * viewport.height;
          minX = Math.min(minX, px);
          minY = Math.min(minY, py);
          maxX = Math.max(maxX, px);
          maxY = Math.max(maxY, py);
        }

        if (!label) return;
        // Round to group overlapping boxes
        const key = `${Math.round(minX)}_${Math.round(minY)}_${Math.round(maxX)}_${Math.round(maxY)}`;
        const existing = computedRegions.find(r => r._key === key);
        if (existing) {
          if (!existing.label.split(', ').includes(label)) {
            existing.label += `, ${label}`;
          }
        } else {
          computedRegions.push({
            _key: key,
            left: minX,
            top: minY,
            width: maxX - minX,
            height: maxY - minY,
            label
          });
        }
      });
    });

    return computedRegions;
  };

  // Render a single page's PDF content onto its canvas, caching its viewport for overlay use.
  // `generation` is the renderAllPages() call this belongs to — if a newer render has started
  // by the time we're about to paint, we bail out instead of racing with it (this race was the
  // root cause of pages occasionally appearing upside-down/garbled right after a document loads
  // or the page/document is reloaded, since two overlapping render() calls on the same canvas
  // with different viewports can interleave and leave the canvas showing a mismatched frame).
  const renderPageContent = async (pageNum, generation) => {
    const page = await pdfDoc.getPage(pageNum);
    if (generation !== renderGenerationRef.current) return; // superseded — abandon
    // Combine the page's own embedded rotation with any manual override for THIS page —
    // most PDFs need their embedded /Rotate value honored to display correctly (it's how
    // the producer intended reading orientation), so we add to it rather than replace it.
    // Each page can have its own independent manual override (documents can mix correctly
    // rotated pages with pages that have no/incorrect rotation metadata).
    const pageRotationOverride = rotation[pageNum] || 0;
    const viewport = page.getViewport({ scale, rotation: (page.rotate + pageRotationOverride) % 360 });
    pageViewportsRef.current[pageNum] = { viewport, pageView: page.view };

    const canvas = canvasRefs.current[pageNum];
    if (!canvas) return;

    // Cancel any still-in-flight render task for this page before starting a new one —
    // pdf.js does not allow two concurrent render() calls against the same canvas, and
    // letting a stale task keep painting after a newer one has started is what produced
    // the occasional upside-down/garbled first paint after (re)loading a document.
    const prevTask = renderTasksRef.current[pageNum];
    if (prevTask) {
      try { prevTask.cancel(); } catch { /* already finished/cancelled */ }
    }

    // Force a genuine backing-store reset before painting. Some PDFs otherwise
    // render incorrectly (notably page 1 appearing upside-down) on the very FIRST
    // paint after a document loads, only correcting once the canvas dimensions
    // actually change (e.g. on manual zoom/resize, which is why that "fixed" it).
    // Explicitly zeroing width/height first forces the browser to discard any
    // stale backing store instead of reusing one that may not match the new frame.
    canvas.width = 0;
    canvas.height = 0;
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const renderTask = page.render({ canvasContext: canvas.getContext('2d'), viewport });
    renderTasksRef.current[pageNum] = renderTask;
    try {
      await renderTask.promise;
    } catch (err) {
      // RenderingCancelledException is expected when a newer render supersedes this one
      if (err?.name !== 'RenderingCancelledException') throw err;
      return;
    } finally {
      if (renderTasksRef.current[pageNum] === renderTask) {
        renderTasksRef.current[pageNum] = null;
      }
    }
    if (generation !== renderGenerationRef.current) return; // superseded after render completed

    const overlayCanvas = overlayRefs.current[pageNum];
    if (overlayCanvas) {
      overlayCanvas.width = viewport.width;
      overlayCanvas.height = viewport.height;
    }
  };

  // Render every page's PDF content, then draw highlight overlays on top
  const renderAllPages = async () => {
    if (!pdfDoc || !totalPages) return;
    const generation = ++renderGenerationRef.current;
    try {
      setRendering(true);
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        await renderPageContent(pageNum, generation);
        if (generation !== renderGenerationRef.current) return; // a newer render call has taken over — stop
      }
      if (generation !== renderGenerationRef.current) return;
      renderAllOverlays();
    } catch (err) {
      console.error('Error rendering pages:', err);
    } finally {
      if (generation === renderGenerationRef.current) setRendering(false);
    }
  };

  // Re-draw just the highlight overlays for every page, using cached viewports (no re-render of PDF content)
  const renderAllOverlays = () => {
    if (!totalPages) return;
    const regionsByPage = {};
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const overlayCanvas = overlayRefs.current[pageNum];
      const info = pageViewportsRef.current[pageNum];
      if (!overlayCanvas || !info) continue;
      const overlayContext = overlayCanvas.getContext('2d');
      overlayContext.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
      const regions = computeAndDrawHighlightsForPage(pageNum, info.viewport, info.pageView, overlayContext);
      if (regions.length) regionsByPage[pageNum] = regions;
    }
    setHighlightRegionsByPage(regionsByPage);
  };

  // Draw a highlight polygon
  const drawHighlight = (context, polygon, viewport) => {
    if (!polygon || polygon.length < 6) return;

    context.beginPath();
    context.strokeStyle = '#3b82f6';  // Blue stroke
    context.fillStyle = 'rgba(59, 130, 246, 0.25)';  // Semi-transparent blue fill
    context.lineWidth = 3;

    // Draw polygon from normalized coordinates
    for (let i = 0; i < polygon.length; i += 2) {
      const x = polygon[i] * viewport.width;
      const y = polygon[i + 1] * viewport.height;
      if (i === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    }

    context.closePath();
    context.fill();
    context.stroke();
  };

  // Navigation handlers — scroll the target page into view (see scrollToPage above)
  const goToPrevPage = () => {
    if (currentPage > 1) {
      scrollToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      scrollToPage(currentPage + 1);
    }
  };

  const adjustZoom = useCallback((delta) => {
    setFitToWidth(false); // Disable fit-to-width when manually zooming
    setScale(prev => Math.min(Math.max((prev || 1.0) + delta, 0.3), 3));
  }, []);

  const zoomIn = useCallback(() => adjustZoom(0.2), [adjustZoom]);
  const zoomOut = useCallback(() => adjustZoom(-0.2), [adjustZoom]);

  const enableFitToWidth = useCallback(() => {
    setFitToWidth(true);
    if (pdfDoc) {
      calculateFitScale(1).then(fitScale => setScale(fitScale));
    }
  }, [pdfDoc, calculateFitScale]);

  const toggleFitToWidth = () => {
    if (fitToWidth) {
      setFitToWidth(false);
    } else {
      enableFitToWidth();
    }
  };

  // Manually rotate the CURRENTLY VIEWED page 90° clockwise, layered on top of that
  // page's own embedded /Rotate value (which is honored by default and is what makes
  // most PDFs display straight). Use this only for pages whose embedded rotation
  // metadata is itself incorrect or missing — rotation is applied per-page since a
  // single document can mix correctly-oriented pages with incorrectly-oriented ones.
  // Persisted (per page, per documentId) so it's remembered next time this document is opened.
  const rotateClockwise = useCallback(() => {
    setRotation(prev => {
      const currentPageRotation = prev[currentPage] || 0;
      const nextPageRotation = (currentPageRotation + 90) % 360;
      const next = { ...prev, [currentPage]: nextPageRotation };
      try {
        window.localStorage.setItem(rotationStorageKey(documentId), JSON.stringify(next));
      } catch {
        // localStorage unavailable (private browsing, quota, etc.) — rotation still
        // applies for this session, just won't persist across reloads
      }
      return next;
    });
    if (fitToWidth) {
      // Recompute fit scale next tick since width/height may have swapped
      setInitialFitDone(false);
    }
  }, [fitToWidth, documentId, currentPage]);

  // ── Document actions ──
  const handleRefresh = useCallback(() => setRetryCount(c => c + 1), []);

  const handleDownload = useCallback(async () => {
    if (!documentId) return;
    try {
      const response = await documentsApi.download(documentId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  }, [documentId, fileName]);

  const handlePrint = useCallback(async () => {
    if (!documentId) return;
    try {
      const response = await documentsApi.download(documentId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => printWindow.print());
      }
    } catch (err) {
      console.error('Error printing document:', err);
    }
  }, [documentId]);

  // ── Click-and-drag to pan the document in any direction ──
  // Move/up listeners are attached to window (not just the scroll container)
  // while dragging, so panning keeps working even when the cursor moves fast
  // enough to leave the container's bounds (e.g. dragging toward the edge to
  // reveal content off to the side) instead of getting "stuck".
  const onPanStart = (e) => {
    const el = containerRef.current;
    if (!el || e.button !== 0) return;
    panRef.current = { active: true, startX: e.clientX, startY: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
    setIsPanning(true);
  };
  const onPanMove = useCallback((e) => {
    if (!panRef.current.active) return;
    const el = containerRef.current;
    if (!el) return;
    el.scrollLeft = panRef.current.scrollLeft - (e.clientX - panRef.current.startX);
    el.scrollTop = panRef.current.scrollTop - (e.clientY - panRef.current.startY);
  }, []);
  const onPanEnd = useCallback(() => {
    if (!panRef.current.active) return;
    panRef.current.active = false;
    setIsPanning(false);
  }, []);

  useEffect(() => {
    if (!isPanning) return;
    window.addEventListener('mousemove', onPanMove);
    window.addEventListener('mouseup', onPanEnd);
    return () => {
      window.removeEventListener('mousemove', onPanMove);
      window.removeEventListener('mouseup', onPanEnd);
    };
  }, [isPanning, onPanMove, onPanEnd]);


  // Ctrl/Cmd + mouse wheel to zoom
  useEffect(() => {
    const el = containerRef.current;
    if (!el || loading || error) return;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        adjustZoom(e.deltaY < 0 ? 0.1 : -0.1);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [adjustZoom, loading, error]);

  // Ctrl/Cmd + (+/-/0) keyboard zoom shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === '=' || e.key === '+') { e.preventDefault(); zoomIn(); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomOut(); }
      else if (e.key === '0') { e.preventDefault(); enableFitToWidth(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomIn, zoomOut, enableFitToWidth]);

  // Show a lightweight skeleton of a document page while the PDF loads, instead of a
  // bare spinner — it fills the viewer's real space and reads as "your document is
  // almost here" rather than a blank wait, at effectively no extra render cost (pure CSS).
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[24rem] bg-gray-100 dark:bg-gray-800 rounded-lg w-full p-6 animate-panel-in">
        <div className="relative w-full max-w-md aspect-[8.5/11] bg-white dark:bg-gray-900 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden p-5 flex flex-col gap-2.5">
          <div className="skeleton h-4 w-2/3 rounded" />
          <div className="skeleton h-2.5 w-1/3 rounded mb-2" />
          {[95, 88, 100, 72, 90, 60].map((w, i) => (
            <div key={i} className="skeleton h-2 rounded" style={{ width: `${w}%` }} />
          ))}
          <div className="skeleton h-2 w-1/2 rounded mt-3" />
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Loader className="h-4 w-4 animate-spin text-navy-500" />
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {loadingProgress || 'Loading document…'}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-4">
        <span className="text-red-600 dark:text-red-400 text-center">{error}</span>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => setRetryCount(c => c + 1)}
            className="px-4 py-2 bg-navy-100 dark:bg-navy-900/40 text-navy-700 dark:text-navy-300 rounded-lg hover:bg-navy-200 dark:hover:bg-navy-900/60 transition-colors"
          >
            Retry
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden w-full h-full min-w-0">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        {/* Left: page navigation — nav arrows are dimmed away (not removed, to avoid layout shift) for single-page documents where they'd otherwise be dead controls */}
        <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
          <button
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            aria-label="Previous page"
            className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 disabled:cursor-not-allowed text-gray-600 dark:text-gray-300 transition-colors ${totalPages <= 1 ? 'disabled:opacity-20' : 'disabled:opacity-40'}`}
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5 px-1 text-sm text-gray-600 dark:text-gray-300">
            <input
              type="number"
              min={1}
              max={totalPages || 1}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              disabled={totalPages <= 1}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const val = parseInt(pageInput, 10);
                  if (!isNaN(val) && val >= 1 && val <= totalPages) {
                    scrollToPage(val);
                  } else {
                    setPageInput(String(currentPage));
                  }
                  e.target.blur();
                }
              }}
              onBlur={() => {
                const val = parseInt(pageInput, 10);
                if (!isNaN(val) && val >= 1 && val <= totalPages) {
                  scrollToPage(val);
                } else {
                  setPageInput(String(currentPage));
                }
              }}
              className="w-9 text-center text-sm font-medium border border-gray-200 dark:border-gray-600 rounded-md px-1 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-500/40 focus:border-navy-400 disabled:opacity-60 disabled:cursor-default [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              title="Go to page"
              aria-label="Page number"
            />
            <span className="text-gray-400 dark:text-gray-500 tabular-nums">/ {totalPages}</span>
          </div>
          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
            className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 disabled:cursor-not-allowed text-gray-600 dark:text-gray-300 transition-colors ${totalPages <= 1 ? 'disabled:opacity-20' : 'disabled:opacity-40'}`}
            title="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Right: zoom + actions */}
        <div className="flex items-center gap-1">
          {/* Zoom group */}
          <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
            <button
              onClick={zoomOut}
              aria-label="Zoom out"
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
              title="Zoom out (Ctrl / ⌘ + −)"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <button
              onClick={toggleFitToWidth}
              aria-label="Reset zoom to fit width"
              className="min-w-[46px] px-1 py-0.5 text-xs font-semibold text-navy-700 dark:text-navy-300 hover:bg-navy-50 dark:hover:bg-navy-900/30 rounded-md tabular-nums transition-colors"
              title="Reset to fit (Ctrl / ⌘ + 0)"
            >
              {scale !== null ? `${Math.round(scale * 100)}%` : '…'}
            </button>
            <button
              onClick={zoomIn}
              aria-label="Zoom in"
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
              title="Zoom in (Ctrl / ⌘ + +)"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            <button
              onClick={toggleFitToWidth}
              aria-label={fitToWidth ? 'Fit to width (currently on)' : 'Fit to width'}
              className={`p-1.5 rounded-md transition-colors ${fitToWidth ? 'bg-navy-100 dark:bg-navy-900/40 text-navy-600 dark:text-navy-300' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
              title={fitToWidth ? 'Fit to width (on)' : 'Fit to width'}
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>

          <span className="h-5 w-px bg-gray-200 dark:bg-gray-700 mx-0.5" />

          {/* Rotation control — fixes documents whose source scan/export embedded an incorrect page rotation */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={rotateClockwise}
              aria-label="Rotate current page 90 degrees clockwise"
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
              title={`Rotate current page 90° clockwise${rotation[currentPage] ? ` (currently ${rotation[currentPage]}°)` : ''}`}
            >
              <RotateCw className="h-4 w-4" />
            </button>
          </div>

          <span className="h-5 w-px bg-gray-200 dark:bg-gray-700 mx-0.5" />

          {/* Document actions */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={handleRefresh}
              aria-label="Reload document"
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
              title="Reload document"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={handleDownload}
              aria-label="Download document"
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={handlePrint}
              aria-label="Print document"
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
              title="Print"
            >
              <Printer className="h-4 w-4" />
            </button>
          </div>

          {onClose && (
            <>
              <span className="h-5 w-px bg-gray-200 dark:bg-gray-700 mx-0.5" />
              <button
                onClick={onClose}
                aria-label="Close document viewer"
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* PDF Canvas Container — continuous vertical scroll through all pages */}
      <div
        ref={containerRef}
        className={`flex-1 overflow-auto p-4 min-w-0 bg-[#525659] dark:bg-[#3a3d40] ${isPanning ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
        onMouseDown={onPanStart}
      >
        {scale === null ? (
          <div className="flex items-center justify-center h-full">
            <Loader className="h-8 w-8 animate-spin text-navy-400" />
            <span className="ml-2 text-gray-300 dark:text-gray-400">Calculating fit...</span>
          </div>
        ) : (
          // NOTE: intentionally NOT `flex flex-col items-center` here. Centering
          // an overflowing flex child clips the "start" side in all browsers
          // (a well-known flexbox/grid overflow bug), which made the left edge
          // of the page unreachable when zoomed in past the container's width -
          // panning/scrolling left had nowhere further to go. Block layout with
          // `mx-auto` centers correctly when the page fits, and simply left-aligns
          // (never clips) once the page is wider than the viewport, so the full
          // page is always reachable by scrolling/panning.
          <div>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <div key={pageNum} className="flex flex-col items-center w-fit mx-auto mb-6 last:mb-0">
                <div
                  ref={(el) => { if (el) pageWrapperRefs.current[pageNum] = el; }}
                  data-page={pageNum}
                  className="relative inline-block bg-white shadow-lg"
                >
                  <canvas ref={(el) => { if (el) canvasRefs.current[pageNum] = el; }} />
                  <canvas
                    ref={(el) => { if (el) overlayRefs.current[pageNum] = el; }}
                    className="absolute top-0 left-0 pointer-events-none"
                  />
                  {/* Hover tooltip overlays for highlighted fields on this page */}
                  {(highlightRegionsByPage[pageNum] || []).map((region, idx) => (
                    <div
                      key={idx}
                      className="absolute group cursor-pointer"
                      style={{
                        left: region.left,
                        top: region.top,
                        width: region.width,
                        height: region.height,
                      }}
                    >
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap max-w-xs truncate opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        {region.label}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  ))}
                </div>
                {totalPages > 1 && (
                  <span className="text-[11px] text-gray-300 dark:text-gray-500 select-none mt-1.5">
                    Page {pageNum} of {totalPages}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Highlighted field info */}
      {highlightedField && (
        <div className="px-4 py-2 bg-navy-50 dark:bg-navy-900/30 border-t border-navy-200 dark:border-navy-800">
          <p className="text-sm text-navy-700 dark:text-navy-300">
            <strong>Highlighted:</strong> {highlightedField.field_name}
            {highlightedField.page_number && ` (Page ${highlightedField.page_number})`}
            {highlightedField.extracted_value && (
              <span className="ml-2 text-gray-600">= "{highlightedField.extracted_value}"</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

export default PDFViewer;
